/** One number formatter for every screen (screens §1.4). No locale, no Intl: output is identical everywhere. */
export type MetricUnit = "usd" | "usdM" | "usdPerShare" | "rate" | "bp" | "index" | "rating" | "multiple";

interface Style {
  /** Multiplier applied to the value before rounding (rates are decimals; ×100 happens only here). */
  readonly scale: number;
  readonly precision: number;
  readonly prefix: string;
  readonly suffix: string;
}

const STYLES: Readonly<Record<MetricUnit, Style>> = {
  usd: { scale: 1e-6, precision: 1, prefix: "$", suffix: "M" }, // whole dollars shown in $M
  usdM: { scale: 1, precision: 0, prefix: "$", suffix: "M" }, // already in $M
  usdPerShare: { scale: 1, precision: 2, prefix: "$", suffix: "" },
  rate: { scale: 100, precision: 1, prefix: "", suffix: "%" },
  bp: { scale: 1, precision: 0, prefix: "", suffix: "bp" },
  index: { scale: 1, precision: 0, prefix: "", suffix: "" },
  rating: { scale: 1, precision: 0, prefix: "", suffix: "" },
  multiple: { scale: 1, precision: 2, prefix: "", suffix: "x" },
};

const MINUS = "−";

const group = (digits: string): string => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/** `precision` overrides the unit's default decimals; `signed` adds "+" to positive values. Negatives use U+2212. */
export function formatMetric(
  value: number,
  unit: MetricUnit,
  opts: { readonly precision?: number; readonly signed?: boolean } = {},
): string {
  const { scale, precision: defaultPrecision, prefix, suffix } = STYLES[unit];
  const [whole = "0", fraction] = (Math.abs(value) * scale).toFixed(opts.precision ?? defaultPrecision).split(".");
  const body = group(whole) + (fraction === undefined ? "" : `.${fraction}`);
  // A value that rounds to zero carries no sign.
  const sign = /[1-9]/.test(body) ? (value < 0 ? MINUS : opts.signed ? "+" : "") : "";
  return `${sign}${prefix}${body}${suffix}`;
}

/** The change at which a rate change stops reading in basis points: ten percentage points (1,000bp). */
const PP_FROM_BP = 1000;

/** How a change in a rate is written: whole basis points, or tenths of a percentage point. */
export type ChangeScale = "bp" | "pp";

/**
 * The rates whose changes read in basis points (screens §1.4): the margin, the loss rate, the cost of deposits and the CET1 ratios, which sit near a few percent and move by bp.
 * Every other rate is a ratio level (coverage, efficiency, economic value, returns, shares, growth) and its change reads in percentage points, on every screen and in the memo.
 */
const BP_MEASURES: ReadonlySet<string> = new Set(["nim", "ncoRate", "cet1Reported", "cet1Marked", "costOfDeposits", "ibDepositCost"]);
export const changeScaleOf = (measure: string): ChangeScale => (BP_MEASURES.has(measure) ? "bp" : "pp");

/**
 * A change in a decimal rate. `bp`: whole basis points ("+6bp"), or percentage points once it reaches ten of them ("+44.00 points", never "+4,402bp");
 * `pp`: hundredths of a percentage point ("−2.90 points"), as points read everywhere (P7-D4).
 */
export function formatRateChange(delta: number, scale: ChangeScale = "bp", opts: { readonly signed?: boolean } = {}): string {
  const signed = opts.signed ?? true;
  const bp = Math.round(delta * 10_000);
  return scale === "pp" || Math.abs(bp) >= PP_FROM_BP ? `${formatMetric(delta * 100, "index", { precision: 2, signed })} points` : formatMetric(bp, "bp", { signed });
}

/**
 * The noise band of a change in a measure: half the smallest step its change is shown in (a spread change shows whole bp, so 0.5bp; a level change a hundredth of a pp, so 0.005pp; $1.4M shows to 0.1, so $50k). A change inside it
 * reads as zero on screen, so no screen may call it a move. In the measure's own units (a rate in decimals, whole dollars for `usd`).
 */
export function changeNoiseBand(unit: MetricUnit, changeScale: ChangeScale = "bp"): number {
  if (unit === "rate") return changeScale === "pp" ? 0.00005 : 0.5 / 10_000;
  const { scale, precision } = STYLES[unit];
  return 0.5 * 10 ** -precision / scale;
}

/** The value as `formatMetric` prints it, back in the unit's own scale: a limit is broken only when the printed reading is past the printed limit (−20.04% beside a −20.0% limit prints equal, so it is no breach). */
export function printedValue(value: number, unit: MetricUnit, precision?: number): number {
  const { scale, precision: defaultPrecision } = STYLES[unit];
  const step = 10 ** (precision ?? defaultPrecision);
  return Math.round(value * scale * step) / step / scale;
}

/**
 * The measures whose level prints to 2 decimals (NIM, NCO, CET1 and the other margin, loss and capital rates, so a table never mixes 1.8% beside 3.64%); every other rate prints to 1.
 * The web's `formatKpi` and the graders both read this one set.
 */
export const TWO_DP: ReadonlySet<string> = new Set(["nim", "ncoRate", "cet1Reported", "cet1Marked", "tier1", "trbc", "leverage", "coe", "roa", "costOfDeposits", "ibDepositCost", "nplRate", "aclRatio"]);

/** The places a rate measure's level prints at (2 for the margin, loss and capital rates, 1 for the other ratios). */
export const printedPlaces = (measure: string): number => (TWO_DP.has(measure) ? 2 : 1);

/**
 * The band under which a change in a measure prints as zero: half one printed step of the measure's level (0.05 pp for a ratio printed at 1 dp, 0.005 pp at 2 dp), in the unit's own scale (a rate in decimals).
 * Other units fall back to `changeNoiseBand`. A rationale is graded against this, so "0.0 pts" is never called a move.
 */
export function printedChangeBand(unit: MetricUnit, measure: string): number {
  return unit === "rate" ? 0.5 * 10 ** -(printedPlaces(measure) + 2) : changeNoiseBand(unit, changeScaleOf(measure));
}

/**
 * Each part and the total rounded to `step` (half away from zero, float noise cleaned); the rounding left over against the printed total goes to the
 * largest part, so the printed parts add exactly to the printed total. Works in whole steps, so cents and basis points add exactly.
 */
export function printedSum(parts: readonly number[], total: number, step: number): { parts: number[]; total: number } {
  const whole = (v: number): number => Math.round(Number((v / step).toPrecision(12)));
  const units = parts.map(whole);
  const totalUnits = whole(total);
  const off = totalUnits - units.reduce((t, v) => t + v, 0);
  if (off !== 0 && units.length > 0) {
    const big = parts.reduce((best, v, i) => (Math.abs(v) > Math.abs(parts[best] ?? 0) ? i : best), 0);
    units[big] = (units[big] ?? 0) + off;
  }
  return { parts: units.map((u) => u * step), total: totalUnits * step };
}
